import React, { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../admin/context/AuthContext';
import publicApi from '../helper/publicApi';
import { getOrderTotals, getItemLineTotal } from '../admin/utils/orderTotals';

const Checkout = () => {
    const navigate = useNavigate();
    const { cartItems, getCartTotal, getCartTax, getCartShipping, getCartGrandTotal, clearCart, getItemPrice } = useCart();
    const { user, isAuthenticated } = useAuth();
    
    const [selectedPayment, setSelectedPayment] = useState("payment1");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderConfirmed, setOrderConfirmed] = useState(null);
    const [generatedCredentials, setGeneratedCredentials] = useState(null);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const transferRib = '007270001437700000014696';

    const getPaymentMethodValue = (paymentId) => {
        // Values stored in backend 'orders.payment_method'
        switch (paymentId) {
            case 'payment1':
                return 'online_payment';
            case 'payment2':
                return 'cheque_payment';
            case 'payment3':
                return 'bank_transfer';
            default:
                return 'online_payment';
        }
    };

    const getPaymentLabel = (paymentMethod) => {
        switch (paymentMethod) {
            case 'online_payment':
                return 'Paiement en ligne';
            case 'cheque_payment':
                return 'Paiement par chèque';
            case 'bank_transfer':
                return 'Paiement par virement';
            case 'cash_on_delivery':
                return 'Paiement à la livraison';
            default:
                return paymentMethod || '';
        }
    };
    
    // Form refs
    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const addressRef = useRef();
    const apartmentRef = useRef();
    const cityRef = useRef();
    const regionRef = useRef();
    const postalCodeRef = useRef();
    const phoneRef = useRef();
    const emailRef = useRef();
    const notesRef = useRef();

    const handlePaymentChange = (event) => {
        setSelectedPayment(event.target.id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        
        try {
            // Get values - for authenticated users, use user data for readonly fields
            const firstName = isAuthenticated ? (user?.name?.split(' ')[0] || '') : (firstNameRef.current?.value || '');
            const lastName = isAuthenticated ? (user?.name?.split(' ').slice(1).join(' ') || '') : (lastNameRef.current?.value || '');
            const email = isAuthenticated ? (user?.email || '') : (emailRef.current?.value || '');
            const phone = isAuthenticated ? (user?.phone || '') : (phoneRef.current?.value || '');
            const city = isAuthenticated ? (user?.city || cityRef.current?.value || '') : (cityRef.current?.value || '');
            
            const orderData = {
                first_name: firstName,
                last_name: lastName,
                company: '',
                email: email,
                phone: phone,
                address: addressRef.current?.value || '',
                apartment: apartmentRef.current?.value || '',
                city: city,
                region: regionRef.current?.value || '',
                postal_code: postalCodeRef.current?.value || '',
                country: 'Maroc',
                notes: notesRef.current?.value || '',
                payment_method: getPaymentMethodValue(selectedPayment),
                user_id: isAuthenticated ? user?.id : null,
                items: cartItems.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity
                }))
            };
            
            // Validate required fields - only prénom, téléphone, and adresse are required
            if (!orderData.first_name || !orderData.phone || !orderData.address) {
                setError('Veuillez remplir tous les champs obligatoires (Prénom, Téléphone, Adresse)');
                setIsSubmitting(false);
                return;
            }

            const response = await publicApi.post('/public/orders', orderData);
            
            if (response.data.success) {
                setOrderConfirmed(response.data.order);
                if (response.data.credentials) {
                    setGeneratedCredentials(response.data.credentials);
                    // Auto-login the guest user
                    if (response.data.token && response.data.user) {
                        localStorage.setItem('adminToken', response.data.token);
                        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
                    }
                }
                clearCart();
            } else {
                setError(response.data.message || 'Erreur lors de la création de la commande');
            }
        } catch (err) {
            console.error('Order error:', err);
            setError(err.message || err.response?.data?.message || 'Erreur lors de la création de la commande');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownloadPDF = () => {
        if (!orderConfirmed) return;
        const totals = getOrderTotals(orderConfirmed);
        
        // Create PDF content
        const printWindow = window.open('', '_blank');
        const logoUrl = `${window.location.origin}/assets/images/logo/blacklogo.png`;
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Commande ${orderConfirmed.order_number}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
                    .header { 
                        text-align: center; 
                        margin-bottom: 40px; 
                        border-bottom: 2px solid #e74c3c; 
                        padding-bottom: 20px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                    .header h1 { color: #e74c3c; margin: 0; font-size: 28px; }
                    .header p { color: #666; margin-top: 10px; }
                    .pdf-logo { width: 200px; height: auto; margin: 0 auto 12px; display: block; }
                    .order-number { background: #f8f9fa; padding: 15px; border-radius: 8px; text-align: center; margin-bottom: 30px; }
                    .order-number span { font-size: 24px; font-weight: bold; color: #e74c3c; }
                    .section { margin-bottom: 30px; }
                    .section h3 { color: #333; border-bottom: 1px solid #ddd; padding-bottom: 10px; margin-bottom: 15px; }
                    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                    .info-item { margin-bottom: 10px; }
                    .info-item label { font-weight: bold; color: #666; display: block; font-size: 12px; }
                    .info-item span { color: #333; font-size: 14px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                    th { background: #f8f9fa; padding: 12px; text-align: left; border-bottom: 2px solid #ddd; }
                    td { padding: 12px; border-bottom: 1px solid #eee; }
                    .total-row { font-weight: bold; font-size: 18px; }
                    .total-row td { border-top: 2px solid #333; padding-top: 15px; }
                    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; }
                    @media print { body { padding: 20px; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="${logoUrl}" alt="DÉLICE BENKIRAN" class="pdf-logo"/>
                    <p>Confirmation de Commande</p>
                </div>
                
                <div class="order-number">
                    <p style="margin: 0; color: #666;">Numéro de commande</p>
                    <span>${orderConfirmed.order_number}</span>
                    <p style="margin: 10px 0 0; color: #666; font-size: 14px;">Date: ${orderConfirmed.created_at}</p>
                </div>

                <div class="section">
                    <h3>Informations Client</h3>
                    <div class="info-grid">
                        <div class="info-item">
                            <label>Nom complet</label>
                            <span>${orderConfirmed.first_name} ${orderConfirmed.last_name}</span>
                        </div>
                        <div class="info-item">
                            <label>Email</label>
                            <span>${orderConfirmed.email}</span>
                        </div>
                        <div class="info-item">
                            <label>Téléphone</label>
                            <span>${orderConfirmed.phone}</span>
                        </div>
                        <div class="info-item">
                            <label>Méthode de paiement</label>
                            <span>${getPaymentLabel(orderConfirmed.payment_method)}</span>
                        </div>
                    </div>
                </div>

                <div class="section">
                    <h3>Adresse de Livraison</h3>
                    <p style="margin: 0;">${orderConfirmed.address}</p>
                    <p style="margin: 5px 0;">${orderConfirmed.city}, ${orderConfirmed.country}</p>
                </div>

                <div class="section">
                    <h3>Détails de la Commande</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Produit</th>
                                <th style="text-align: center;">Quantité</th>
                                <th style="text-align: right;">Prix unitaire</th>
                                <th style="text-align: right;">Sous-total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${orderConfirmed.items.map(item => `
                                <tr>
                                    <td>${item.product_name}</td>
                                    <td style="text-align: center;">${item.quantity}</td>
                                    <td style="text-align: right;">${parseFloat(item.price).toFixed(2)} DH</td>
                                    <td style="text-align: right;">${getItemLineTotal(item).toFixed(2)} DH</td>
                                </tr>
                            `).join('')}
                            <tr>
                                <td colspan="3" style="text-align: right; padding-top: 16px;">Sous-total</td>
                                <td style="text-align: right; padding-top: 16px;">${totals.subtotal.toFixed(2)} DH</td>
                            </tr>
                            <tr>
                                <td colspan="3" style="text-align: right;">TVA (20%)</td>
                                <td style="text-align: right;">${totals.tax.toFixed(2)} DH</td>
                            </tr>
                            <tr>
                                <td colspan="3" style="text-align: right;">Livraison</td>
                                <td style="text-align: right;">${totals.shipping.toFixed(2)} DH</td>
                            </tr>
                            <tr class="total-row">
                                <td colspan="3" style="text-align: right;">Total</td>
                                <td style="text-align: right;">${totals.grandTotal.toFixed(2)} DH</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="footer">
                    <p>Merci pour votre commande !</p>
                    <p style="font-size: 12px; margin-bottom: 6px;">
                        Pour toute question, contactez-nous à <strong>contact@delicesbf.com</strong>
                    </p>
                    <p style="font-size: 12px; margin: 0; line-height: 1.25;">
                        06 61 70 00 52 <span style="padding: 0 6px;">|</span> 06 66 94 42 24
                    </p>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();

        // Ensure images are loaded before printing
        const waitForImages = () => {
            const images = Array.from(printWindow.document.images || []);
            if (images.length === 0) return Promise.resolve();
            return Promise.all(
                images.map(
                    (img) =>
                        new Promise((resolve) => {
                            if (img.complete) return resolve();
                            img.onload = () => resolve();
                            img.onerror = () => resolve();
                        })
                )
            );
        };

        // Some browsers need a tick after document.close()
        setTimeout(() => {
            waitForImages().finally(() => {
                printWindow.focus();
                printWindow.print();
            });
        }, 50);
    };

    // Order Confirmation View
    if (orderConfirmed) {
        const confirmedTotals = getOrderTotals(orderConfirmed);
        return (
            <section className="checkout py-80">
                <div className="container container-lg">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            {/* Success Header */}
                            <div className="text-center mb-48">
                                <div className="w-80 h-80 bg-success-100 rounded-circle flex-center mx-auto mb-24">
                                    <i className="ph ph-check-circle text-success-600" style={{ fontSize: '48px' }}></i>
                                </div>
                                <h2 className="mb-16">Commande Confirmée !</h2>
                                <p className="text-gray-600 text-lg">Merci pour votre commande. Voici les détails de votre achat.</p>
                            </div>

                            {/* Order Number Card */}
                            <div className="bg-main-50 rounded-16 p-32 mb-32 text-center">
                                <p className="text-gray-600 mb-8">Numéro de commande</p>
                                <h3 className="text-main-600 mb-8" style={{ fontSize: '28px', letterSpacing: '2px' }}>
                                    {orderConfirmed.order_number}
                                </h3>
                                <p className="text-gray-500 text-sm">Date: {orderConfirmed.created_at}</p>
                            </div>

                            {/* Generated Credentials for Guest Users */}
                            {generatedCredentials && (
                                <div className="bg-warning-50 border border-warning-200 rounded-16 p-32 mb-32">
                                    <div className="d-flex align-items-center mb-20">
                                        <div className="w-48 h-48 bg-warning-100 rounded-circle flex-center me-16">
                                            <i className="ph ph-key text-warning-600" style={{ fontSize: '24px' }}></i>
                                        </div>
                                        <div>
                                            <h5 className="mb-4 text-gray-900">Votre compte a été créé !</h5>
                                            <p className="text-gray-600 text-sm mb-0">Conservez ces informations pour suivre votre commande</p>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-12 p-20">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center">
                                                    <i className="ph ph-envelope text-gray-500 me-12" style={{ fontSize: '20px' }}></i>
                                                    <div>
                                                        <span className="text-gray-500 d-block text-xs text-uppercase">Email</span>
                                                        <span className="text-gray-900 fw-semibold">{generatedCredentials.email}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center">
                                                    <i className="ph ph-lock-key text-gray-500 me-12" style={{ fontSize: '20px' }}></i>
                                                    <div className="flex-grow-1">
                                                        <span className="text-gray-500 d-block text-xs text-uppercase">Mot de passe</span>
                                                        <div className="d-flex align-items-center gap-8">
                                                            <span className="text-gray-900 fw-semibold">
                                                                {showPassword ? generatedCredentials.password : '••••••••'}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                className="border-0 bg-transparent p-0 d-flex align-items-center text-gray-500 hover-text-main-600"
                                                                style={{ cursor: 'pointer' }}
                                                                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                                            >
                                                                <i className={`ph ${showPassword ? 'ph-eye-slash' : 'ph-eye'}`} style={{ fontSize: '18px' }} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-warning-700 text-sm mt-16 mb-0">
                                        <i className="ph ph-warning me-8"></i>
                                        Important : Notez ces informations. Vous en aurez besoin pour accéder à votre compte.
                                    </p>
                                </div>
                            )}

                            <div className="row g-4">
                                {/* Customer Info */}
                                <div className="col-md-6">
                                    <div className="border border-gray-100 rounded-16 p-24 h-100">
                                        <h5 className="mb-20 pb-16 border-bottom border-gray-100">
                                            <i className="ph ph-user me-8"></i>
                                            Informations Client
                                        </h5>
                                        <div className="mb-12">
                                            <span className="text-gray-500 d-block text-sm">Nom complet</span>
                                            <span className="text-gray-900 fw-medium">{orderConfirmed.first_name} {orderConfirmed.last_name}</span>
                                        </div>
                                        <div className="mb-12">
                                            <span className="text-gray-500 d-block text-sm">Email</span>
                                            <span className="text-gray-900 fw-medium">{orderConfirmed.email}</span>
                                        </div>
                                        <div className="mb-12">
                                            <span className="text-gray-500 d-block text-sm">Téléphone</span>
                                            <span className="text-gray-900 fw-medium">{orderConfirmed.phone}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 d-block text-sm">Paiement</span>
                                            <span className="text-gray-900 fw-medium">{getPaymentLabel(orderConfirmed.payment_method)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Delivery Address */}
                                <div className="col-md-6">
                                    <div className="border border-gray-100 rounded-16 p-24 h-100">
                                        <h5 className="mb-20 pb-16 border-bottom border-gray-100">
                                            <i className="ph ph-map-pin me-8"></i>
                                            Adresse de Livraison
                                        </h5>
                                        <p className="text-gray-900 mb-8">{orderConfirmed.address}</p>
                                        <p className="text-gray-600">{orderConfirmed.city}, {orderConfirmed.country}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="border border-gray-100 rounded-16 p-24 mt-32">
                                <h5 className="mb-20 pb-16 border-bottom border-gray-100">
                                    <i className="ph ph-shopping-bag me-8"></i>
                                    Détails de la Commande
                                </h5>
                                
                                <div className="table-responsive">
                                    <table className="table" style={{ minWidth: '500px' }}>
                                        <thead>
                                            <tr>
                                                <th className="text-gray-600 fw-medium py-12">Produit</th>
                                                <th className="text-gray-600 fw-medium py-12 text-center">Quantité</th>
                                                <th className="text-gray-600 fw-medium py-12 text-end">Prix</th>
                                                <th className="text-gray-600 fw-medium py-12 text-end">Sous-total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderConfirmed.items.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="py-16">
                                                        <span className="text-gray-900 fw-medium">{item.product_name}</span>
                                                    </td>
                                                    <td className="py-16 text-center">{item.quantity}</td>
                                                    <td className="py-16 text-end">{parseFloat(item.price).toFixed(2)} DH</td>
                                                    <td className="py-16 text-end fw-semibold">{getItemLineTotal(item).toFixed(2)} DH</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="3" className="py-8 text-end text-gray-600">Sous-total</td>
                                                <td className="py-8 text-end fw-medium">
                                                    {confirmedTotals.subtotal.toFixed(2)} DH
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="3" className="py-8 text-end text-gray-600">TVA (20%)</td>
                                                <td className="py-8 text-end fw-medium">
                                                    {confirmedTotals.tax.toFixed(2)} DH
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan="3" className="py-8 text-end text-gray-600">Livraison</td>
                                                <td className="py-8 text-end fw-medium">
                                                    {confirmedTotals.shipping.toFixed(2)} DH
                                                </td>
                                            </tr>
                                            <tr className="border-top border-gray-200">
                                                <td colSpan="3" className="py-16 text-end fw-bold text-lg">Total</td>
                                                <td className="py-16 text-end fw-bold text-lg text-main-600">
                                                    {confirmedTotals.grandTotal.toFixed(2)} DH
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex-center gap-16 mt-40">
                                <button 
                                    onClick={handleDownloadPDF}
                                    className="btn btn-outline-main py-16 px-32 rounded-8 flex-align gap-8"
                                >
                                    <i className="ph ph-download-simple"></i>
                                    Télécharger PDF
                                </button>
                                {generatedCredentials && (
                                    <Link 
                                        to="/my-orders" 
                                        className="btn btn-main py-16 px-32 rounded-8 flex-align gap-8"
                                    >
                                        <i className="ph ph-package"></i>
                                        Voir mes commandes
                                    </Link>
                                )}
                                <Link 
                                    to="/shop" 
                                    className="btn btn-outline-gray py-16 px-32 rounded-8 flex-align gap-8"
                                >
                                    <i className="ph ph-shopping-cart"></i>
                                    Continuer vos achats
                                </Link>
                            </div>

                            {/* Contact info is shown in the PDF print window only */}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (cartItems.length === 0) {
        return (
            <section className="checkout py-80">
                <div className="container container-lg text-center">
                    <h4 className="mb-24">Votre panier est vide</h4>
                    <Link to="/shop" className="btn btn-main py-16 px-32 rounded-8">
                        Continuer vos achats
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <section className="checkout py-80">
            <div className="container container-lg">
                <div className="border border-gray-100 rounded-8 px-30 py-20 mb-40">
                    <span className="">
                        Avez-vous un coupon ?{" "}
                        <Link
                            to="/cart"
                            className="fw-semibold text-gray-900 hover-text-decoration-underline hover-text-main-600"
                        >
                            Cliquez ici pour entrer votre code
                        </Link>{" "}
                    </span>
                </div>
                <div className="row">
                    <div className="col-xl-9 col-lg-8">
                        <form action="#" className="pe-xl-5">
                            <div className="row gy-3">
                                <div className="col-sm-6 col-xs-6">
                                    <input
                                        ref={firstNameRef}
                                        type="text"
                                        className={`common-input border-gray-100 ${isAuthenticated ? 'bg-gray-50' : ''}`}
                                        placeholder="Prénom *"
                                        defaultValue={isAuthenticated ? user?.name?.split(' ')[0] || '' : ''}
                                        readOnly={isAuthenticated}
                                        required
                                    />
                                </div>
                                <div className="col-sm-6 col-xs-6">
                                    <input
                                        ref={lastNameRef}
                                        type="text"
                                        className={`common-input border-gray-100 ${isAuthenticated ? 'bg-gray-50' : ''}`}
                                        placeholder="Nom"
                                        defaultValue={isAuthenticated ? user?.name?.split(' ').slice(1).join(' ') || '' : ''}
                                        readOnly={isAuthenticated}
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        type="text"
                                        className="common-input border-gray-100 bg-gray-50"
                                        placeholder="Maroc (MA)"
                                        defaultValue="Maroc"
                                        readOnly
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={addressRef}
                                        type="text"
                                        className="common-input border-gray-100"
                                        placeholder="Numéro et nom de rue *"
                                        defaultValue={isAuthenticated ? user?.address || '' : ''}
                                        required
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={apartmentRef}
                                        type="text"
                                        className="common-input border-gray-100"
                                        placeholder="Appartement, suite, unité, etc. (Optionnel)"
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={cityRef}
                                        type="text"
                                        className="common-input border-gray-100"
                                        placeholder="Ville"
                                        defaultValue={isAuthenticated ? user?.city || '' : ''}
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={regionRef}
                                        type="text"
                                        className="common-input border-gray-100"
                                        placeholder="Région"
                                        defaultValue={isAuthenticated ? user?.region || '' : ''}
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={postalCodeRef}
                                        type="text"
                                        className="common-input border-gray-100"
                                        placeholder="Code postal"
                                        defaultValue={isAuthenticated ? user?.postal_code || '' : ''}
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={phoneRef}
                                        type="tel"
                                        className={`common-input border-gray-100 ${isAuthenticated ? 'bg-gray-50' : ''}`}
                                        placeholder="Téléphone *"
                                        defaultValue={isAuthenticated ? user?.phone || '' : ''}
                                        readOnly={isAuthenticated}
                                        required
                                    />
                                </div>
                                <div className="col-12">
                                    <input
                                        ref={emailRef}
                                        type="email"
                                        className={`common-input border-gray-100 ${isAuthenticated ? 'bg-gray-50' : ''}`}
                                        placeholder="Adresse e-mail"
                                        defaultValue={isAuthenticated ? user?.email || '' : ''}
                                        readOnly={isAuthenticated}
                                    />
                                </div>
                                <div className="col-12">
                                    <div className="my-40">
                                        <h6 className="text-lg mb-24">Informations supplémentaires</h6>
                                        <input
                                            ref={notesRef}
                                            type="text"
                                            className="common-input border-gray-100"
                                            placeholder="Notes concernant votre commande, par ex. notes spéciales pour la livraison."
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="col-xl-3 col-lg-4">
                        <div className="checkout-sidebar">
                            <div className="bg-color-three rounded-8 p-24 text-center">
                                <span className="text-gray-900 text-xl fw-semibold">
                                    Vos Commandes
                                </span>
                            </div>
                            <div className="border border-gray-100 rounded-8 px-24 py-40 mt-24">
                                <div className="mb-32 pb-32 border-bottom border-gray-100 flex-between gap-8">
                                    <span className="text-gray-900 fw-medium text-xl font-heading-two">
                                        Produit
                                    </span>
                                    <span className="text-gray-900 fw-medium text-xl font-heading-two">
                                        Sous-total
                                    </span>
                                </div>
                                
                                {cartItems.map((item) => (
                                    <div key={item.id} className="flex-between gap-24 mb-32">
                                        <div className="flex-align gap-12">
                                            <span className="text-gray-900 fw-normal text-md font-heading-two w-144">
                                                {item.name}
                                            </span>
                                            <span className="text-gray-900 fw-normal text-md font-heading-two">
                                                <i className="ph-bold ph-x" />
                                            </span>
                                            <span className="text-gray-900 fw-semibold text-md font-heading-two">
                                                {item.quantity}
                                            </span>
                                        </div>
                                        <span className="text-gray-900 fw-bold text-md font-heading-two">
                                            {(getItemPrice(item) * item.quantity).toFixed(2)} DH
                                        </span>
                                    </div>
                                ))}
                                
                                <div className="border-top border-gray-100 pt-30 mt-30">
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                                            Sous-total
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {getCartTotal().toFixed(2)} DH
                                        </span>
                                    </div>
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                                            Livraison
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {getCartShipping() === 0 ? 'Gratuite' : `${getCartShipping().toFixed(2)} DH`}
                                        </span>
                                    </div>
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                                            TVA (20%)
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {getCartTax().toFixed(2)} DH
                                        </span>
                                    </div>
                                    <div className="mb-0 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                                            Total
                                        </span>
                                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                                            {getCartGrandTotal().toFixed(2)} DH
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-32">
                                <div className="payment-item">
                                    <div className="form-check common-check common-radio py-16 mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="payment"
                                            id="payment1"
                                            checked={selectedPayment === 'payment1'}
                                            onChange={handlePaymentChange}
                                        />
                                        <label
                                            className="form-check-label fw-semibold text-neutral-600"
                                            htmlFor="payment1"
                                        >
                                            Paiement en ligne
                                        </label>
                                    </div>
                                    {selectedPayment === 'payment1' && (
                                        <div className="payment-item__content checkout-payment-content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                                            <p className="text-gray-800">
                                                <i className="ph ph-check-circle text-success-600 me-8"></i>
                                                Paiement en ligne sécurisé. Veuillez valider la commande pour finaliser le paiement.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="payment-item">
                                    <div className="form-check common-check common-radio py-16 mb-0">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="payment"
                                            id="payment3"
                                            checked={selectedPayment === 'payment3'}
                                            onChange={handlePaymentChange}
                                        />
                                        <label
                                            className="form-check-label fw-semibold text-neutral-600"
                                            htmlFor="payment3"
                                        >
                                            Paiement par virement
                                        </label>
                                    </div>
                                    {selectedPayment === 'payment3' && (
                                        <div className="payment-item__content checkout-payment-content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                                            <p className="text-gray-800 mb-12">
                                                <i className="ph ph-check-circle text-success-600 me-8"></i>
                                                Paiement par virement. Après validation, suivez les instructions pour finaliser le règlement.
                                            </p>
                                            <div className="d-flex align-items-center justify-content-between gap-12 flex-wrap">
                                                <div className="text-gray-700">
                                                    <div className="text-xs text-gray-500 mb-4">RIB</div>
                                                    <div className="fw-semibold" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                                                        {transferRib}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-main"
                                                    onClick={async () => {
                                                        try {
                                                            if (navigator?.clipboard?.writeText) {
                                                                await navigator.clipboard.writeText(transferRib);
                                                            } else {
                                                                const textarea = document.createElement('textarea');
                                                                textarea.value = transferRib;
                                                                textarea.style.position = 'fixed';
                                                                textarea.style.opacity = '0';
                                                                document.body.appendChild(textarea);
                                                                textarea.focus();
                                                                textarea.select();
                                                                document.execCommand('copy');
                                                                document.body.removeChild(textarea);
                                                            }
                                                        } catch (e) {
                                                            // no-op: clipboard may be blocked by browser permissions
                                                        }
                                                    }}
                                                >
                                                    Copier
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-32 pt-32 border-top border-gray-100">
                                <p className="text-gray-500">
                                    Vos données personnelles seront utilisées pour traiter votre commande, améliorer votre expérience sur ce site web, et à d'autres fins décrites dans notre{" "}
                                    <Link to="#" className="text-main-600 text-decoration-underline checkout-privacy-link">
                                        {" "}
                                        politique de confidentialité
                                    </Link>{" "}
                                    .
                                </p>
                            </div>
                            
                            {error && (
                                <div className="alert alert-danger mt-24 rounded-8">
                                    <i className="ph ph-warning me-8"></i>
                                    {error}
                                </div>
                            )}
                            
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className={`btn btn-main mt-40 py-18 w-100 rounded-8 mt-56 checkout-place-order-btn`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-8" role="status"></span>
                                        Traitement en cours...
                                    </>
                                ) : (
                                    'Passer la commande'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Checkout
